import { ResourceType } from '../../enumerations/ResourceType';
import { post } from '../APIVersion1';
import { VersionedArtifactResource } from '../artifactResourceTypes';
import { NewRootVersionMutateObject, NewRootVersionMutateObjectSchema, NewVersionMutateObject, NewVersionMutateObjectSchema } from '../mutateObjects';
import { PersistableSchemaType } from '../persistableResourceTypes';
import { ParamConstants } from '../types';
import { useMutationWithDelay } from '../useMutationWithDelay';
import { useMultiResourceSingleParentedResourceAdapterBaseActions } from './useMultiResourceSingleParentedResourceAdapterBaseActions';
import { useVersionActions } from './useVersionActions';

export function useMultiResourceSingleParentedVersionedResourceAdapterActions<R extends VersionedArtifactResource>(
    apiPostfix: string,
    resourceType: ResourceType,
    resourceSchema: PersistableSchemaType<R>
) {
    const actions = useMultiResourceSingleParentedResourceAdapterBaseActions(apiPostfix, resourceType, resourceSchema);
    const versionActions = useVersionActions(apiPostfix, resourceType);

    const useCreateRootVersion = () => {
        return useMutationWithDelay(
            ({ parentId, resource }: NewRootVersionMutateObject<R>) =>
                post(`${apiPostfix}?${ParamConstants.PARENT_ID_REQUEST_PARAM_NAME}=${parentId}`, JSON.stringify(resource ?? {})).then(result =>
                    actions.processMutationResponse(result, 'ADD')
                ),
            NewRootVersionMutateObjectSchema
        );
    };

    const useCreateNewVersion = () => {
        return useMutationWithDelay(
            ({ precedingVersion }: NewVersionMutateObject) =>
                post(`${apiPostfix}/new?${ParamConstants.PRECEDING_VERSION_REQUEST_PARAM_NAME}=${precedingVersion}`, '').then(result =>
                    actions.processMutationResponse(result, 'ADD')
                ),
            NewVersionMutateObjectSchema
        );
    };

    return {
        useValidateResource: actions.useValidateResource,
        useValidateAllResourcesSingleParented: actions.useValidateAllResourcesSingleParented,
        useGetResource: actions.useGetResource,
        useUpdateResource: actions.useUpdateResource,
        useDeleteResource: actions.useDeleteResource,
        useGetAllResources: actions.useGetAllResources,
        processMutationResponse: actions.processMutationResponse,
        processGetResponse: actions.processGetResponse,
        processGetCollectionResponse: actions.processGetCollectionResponse,
        processGetResourceResponse: actions.processGetResourceResponse,
        useCreateNewVersion,
        useCreateRootVersion,
        ...versionActions,
    };
}
